import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Joi from "joi";
import axios from "axios";
import helpers from "../../../../lib/crypto";
import { useFunctionContext } from "../../../context/FunctionalContext";
import countries from "world-countries";
import { ChevronDown, Tally1, X } from "lucide-react";
import confetti from "canvas-confetti";
import countryList from "../../../../utils/data";

const API_URL = import.meta.env.VITE_API_URL;

// ----------------- Joi Schema -----------------
const schema = Joi.object({
  lead_name: Joi.string().trim().min(3).max(30).optional().allow(""),
  email: Joi.string()
    .email({ tlds: { allow: false } })
    .trim()
    .min(10)
    .max(255)
    .required()
    .messages({ "string.email": "Email should be valid" }),
  listing_name: Joi.string().trim().optional().allow(""),
  contact_number: Joi.string().trim().allow(null, "").required(),
  type: Joi.string().trim().optional().allow(""),
  price: Joi.string().optional().allow(""),
  currency_symbol: Joi.string().optional().allow(""),
  area: Joi.string().optional().allow(""),
  listing_type: Joi.string().optional().allow(""),
  address: Joi.string().optional().allow(""),
  city: Joi.string().optional().allow(""),
  country: Joi.string().optional().allow(""),
  comments: Joi.string().min(3).max(2000).optional().allow(""),
});

// ----------------- Component -----------------
export default function ListYourProperty() {
  const { ORG_ID } = useFunctionContext();

  const [formData, setFormData] = useState({
    lead_name: "",
    email: "",
    listing_name: "",
    type: "",
    price: "",
    currency_symbol: "",
    area: "",
    contact_number: "",
    listing_type: "",
    address: "",
    city: "",
    country: "",
    comments: "",
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCurrencyOpen, setIsCurrencyOpen] = useState(false);
  const [currencySearch, setCurrencySearch] = useState("");
  const [isCountryOpen, setIsCountryOpen] = useState(false);
  const [countrySearch, setCountrySearch] = useState("");
  const [phoneNumberModalOpen, setPhoneNumberModalOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [selectedCode, setSelectedCode] = useState("+91");
  const [selectedEmoji, setSelectedEmoji] = useState("🇮🇳"); // Default emoji for India

  // ----------------- Country → Currency Map -----------------
  const countryCurrencyMap = countries.reduce((acc, country) => {
    const code = country.currencies ? Object.keys(country.currencies)[0] : null;
    if (code) {
      acc[country.name.common] = {
        name: country.name.common,
        code,
        symbol: country.currencies[code]?.symbol || code,
      };
    }
    return acc;
  }, {});

  // ----------------- Handlers -----------------
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCurrencySelect = (code) => {
    setFormData((prev) => ({ ...prev, currency_symbol: code }));
    setIsCurrencyOpen(false);
  };

  const handleCountrySelect = (country) => {
    setFormData((prev) => ({
      ...prev,
      country: country.name.common,
      currency_symbol: countryCurrencyMap[country.name.common]?.code || "",
    }));
    setIsCountryOpen(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrors({});

    const { error } = schema.validate(formData, { abortEarly: false });
    if (error) {
      const fieldErrors = {};
      error.details.forEach((err) => {
        fieldErrors[err.path.join(".")] = err.message;
      });
      setErrors(fieldErrors);
      setLoading(false);
      return;
    }

    try {
      const payload = {
        ...formData,
        files: [], // empty string if no files
        contact_number: `${selectedCode}-${formData.contact_number}`,
        assigned_to: [], // empty array if not assigned
        source: "Realty",
        key: "scanglobal",
        status: "New",
        next_follow_up: new Date().toISOString(),
        route_action: 1,
        organisation_id: ORG_ID || "",
        admin_type: "1",
        price: formData.price || "",
      };

      const encrypted = helpers.encryptobj(payload);

      const response = await axios.post(
        `${API_URL}/admin/add_update_lead`,
        { enc: encrypted },
        { headers: { "Content-Type": "application/json" } }
      );

      if (response.status === 200) {
        setSuccessMessage("Property listed successfully!");
        confetti({
          particleCount: 100,
          spread: 70,
          origin: { y: 0.6 },
        });
        // reset form
        setFormData({
          lead_name: "",
          email: "",
          listing_name: "",
          contact_number: "",
          type: "",
          price: "",
          currency_symbol: "",
          area: "",
          listing_type: "",
          address: "",
          city: "",
          country: "",
          comments: "",
        });
      }
    } catch {
      setErrors({ general: "Network or server error. Please try again." });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!formData?.contact_number?.includes("-")) return; // ✅ skip if only number

    const [code, number] = formData.contact_number.split("-");
    const foundCountry = countryList.find((c) => c.dial_code === code);

    if (foundCountry) {
      setSelectedCode(foundCountry.dial_code);
      setSelectedEmoji(foundCountry.emoji);
      setFormData((prev) => ({ ...prev, contact_number: number }));
    }
  }, [formData?.contact_number, countryList]);

  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(""), 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  return (
    <div className="flex justify-center">
      <button
        className="bg-gold text-black btn !py-1 !rounded-lg"
        onClick={() => setIsModalOpen(true)}
      >
        List Your Property
      </button>

      <AnimatePresence>
        {isModalOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed p-3 inset-0 z-[100] flex items-center justify-center bg-black/90 backdrop-blur-md"
          >
            <motion.div
              initial={{ y: -200, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: -200, opacity: 0 }}
              transition={{ duration: 0.4, ease: "easeInOut" }}
              className="bg-slate-950 text-white rounded-xl border border-slate-700 w-full max-w-2xl p-6 relative overflow-y-auto max-h-[90vh]"
            >
              <button
                onClick={() => setIsModalOpen(false)}
                className="absolute top-3 right-5 text-gray-500 hover:text-white"
              >
                ✕
              </button>

              <h2 className="text-2xl font-semibold mb-4">Add Your Property</h2>

              <form
                onSubmit={handleSubmit}
                className="sm:grid space-y-4 sm:space-y-0 grid-cols-1 md:grid-cols-2 gap-4"
              >
                <div>
                  <input
                    name="lead_name"
                    value={formData.lead_name}
                    onChange={handleChange}
                    placeholder="User Name"
                    className="input-class col-span-2"
                  />
                  {errors.lead_name && (
                    <p className="text-red-400 text-sm">{errors.lead_name}</p>
                  )}
                </div>

                <div>
                  <input
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="User Email *"
                    className="input-class col-span-2"
                  />
                  {errors.email && (
                    <p className="text-red-400 text-sm">{errors.email}</p>
                  )}
                </div>

                <input
                  name="listing_name"
                  value={formData.listing_name}
                  onChange={handleChange}
                  placeholder="Listing Name"
                  className="input-class"
                />

                <input
                  name="type"
                  value={formData.type}
                  onChange={handleChange}
                  placeholder="Type (Apartment / Villa / Plot)"
                  className="input-class"
                />

                <input
                  name="price"
                  type="number"
                  value={formData.price}
                  onChange={handleChange}
                  placeholder="Price"
                  className="input-class"
                />

                {/* Currency Dropdown */}
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setIsCurrencyOpen(!isCurrencyOpen)}
                    className="w-full input-class flex justify-between items-center"
                  >
                    {formData.currency_symbol || "Select Currency"}
                    <ChevronDown
                      size={18}
                      className={isCurrencyOpen ? "rotate-180" : ""}
                    />
                  </button>
                  <AnimatePresence>
                    {isCurrencyOpen && (
                      <motion.ul
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        className="absolute z-50 mt-2 w-full bg-slate-900 rounded-lg shadow max-h-48 overflow-y-auto"
                      >
                        <div className="sticky top-0 bg-slate-900 p-2">
                          <input
                            type="text"
                            placeholder="Search currency..."
                            value={currencySearch}
                            onChange={(e) => setCurrencySearch(e.target.value)}
                            className="w-full p-2 text-sm outline-none text-black"
                          />
                        </div>
                        {Object.values(countryCurrencyMap)
                          .filter((c) =>
                            c.code
                              .toLowerCase()
                              .includes(currencySearch.toLowerCase())
                          )
                          .map((c, i) => (
                            <li
                              key={i}
                              onClick={() => handleCurrencySelect(c.code)}
                              className="px-4 py-2 hover:bg-slate-800 cursor-pointer"
                            >
                              {c.code} ({c.symbol})
                            </li>
                          ))}
                      </motion.ul>
                    )}
                  </AnimatePresence>
                </div>

                <input
                  name="area"
                  value={formData.area}
                  onChange={handleChange}
                  placeholder="Area (sqft)"
                  className="input-class"
                />

                <select
                  name="listing_type"
                  value={formData.listing_type}
                  onChange={handleChange}
                  className="input-class"
                >
                  <option value="">Select Listing Type</option>
                  <option value="Rent">Rent</option>
                  <option value="Sale">Sale</option>
                  <option value="Hot Listing">Hot Listing</option>
                  <option value="Offplan">Offplan</option>
                  <option value="Ready To Move">Ready To Move</option>
                </select>

                <input
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  placeholder="Address"
                  className="input-class"
                />

                {/* phone number */}
                <div
                  className="flex items-center border rounded-md px-2 max-w-[400px] input-class"
                  style={{ borderradius: "0.5rem" }}
                >
                  {/* Country phone number */}
                  <div className="relative">
                    <button
                      type="button"
                      className="flex items-center pr-2 text-gray-600 font-medium"
                      onClick={() =>
                        setPhoneNumberModalOpen(!phoneNumberModalOpen)
                      }
                    >
                      <ChevronDown className="size-3" />
                      <span className="mr-1 font-medium text-sm">
                        {selectedEmoji}
                      </span>
                      <span className="font-medium text-sm">
                        {selectedCode}
                      </span>
                      <span className="ml-1">|</span>
                    </button>

                    {/* Show dropdown only when modalOpen = true */}
                    {phoneNumberModalOpen && (
                      <div className="absolute mt-1 bg-slate-900 rounded shadow-lg z-10 max-h-60 overflow-y-auto w-64">
                        {/* Header with title + close button */}

                        <div className="p-2 sticky top-0 bg-slate-900">
                          <div className="flex items-center justify-between px-3 py-4 relative">
                            <button
                              type="button"
                              className="text-gray-500 hover:text-gray-700 absolute right-0 top-0 py-2"
                              onClick={() => {
                                setPhoneNumberModalOpen(false);
                                setSearch("");
                              }}
                            >
                              <X size={18} />
                            </button>
                          </div>
                          <input
                            type="text"
                            placeholder="Search..."
                            className="w-full border border-slate-600/40 rounded px-2 py-1 text-sm focus:outline-none bg-transparent"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                          />
                        </div>

                        {/* Filtered country list */}
                        {countryList
                          .filter(
                            (c) =>
                              c.name
                                .toLowerCase()
                                .includes(search.toLowerCase()) ||
                              c.dial_code.includes(search)
                          )
                          .map((c) => (
                            <div
                              key={c.code}
                              className="px-2 py-2 cursor-pointer hover:bg-slate-800 flex justify-between items-center"
                              onClick={() => {
                                setPhoneNumberModalOpen(false);
                                setSearch(""); // clear search after selection
                                setSelectedCode(c.dial_code);
                                setSelectedEmoji(c.emoji);
                              }}
                            >
                              <div className="flex items-center">
                                <span className="mr-2">{c.emoji}</span>
                                <span>{c.name}</span>
                              </div>
                              <span className="text-gray-600">
                                {c.dial_code}
                              </span>
                            </div>
                          ))}
                      </div>
                    )}
                  </div>

                  {/* Phone Input */}
                  <input
                    id="contact_number"
                    name="contact_number"
                    type="tel"
                    // value={formData.contact_number}
                    value={
                      formData.contact_number.split("-")[1] || // take only number part
                      formData.contact_number
                    }
                    onChange={handleChange}
                    className="flex-1 ml-2 bg-transparent focus:outline-none focus:ring-0 border-none text-sm"
                    placeholder="Enter contact number"
                    autoCapitalize="none"
                    autoComplete="off"
                    // maxLength={10}
                    style={{ border: "none", outline: "none" }}
                    autoFocus={false}
                    inputMode="tel"
                  />
                </div>

                <input
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  placeholder="City"
                  className="input-class"
                />

                {/* Country Dropdown */}
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setIsCountryOpen(!isCountryOpen)}
                    className="w-full input-class flex justify-between items-center"
                  >
                    {formData.country || "Select Country"}
                    <ChevronDown
                      size={18}
                      className={isCountryOpen ? "rotate-180" : ""}
                    />
                  </button>
                  <AnimatePresence>
                    {isCountryOpen && (
                      <motion.ul
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        className="absolute mt-2 w-full bg-slate-900 rounded-lg shadow max-h-52 overflow-y-auto"
                      >
                        <div className="sticky top-0 bg-slate-900 p-2">
                          <input
                            type="text"
                            placeholder="Search country..."
                            value={countrySearch}
                            onChange={(e) => setCountrySearch(e.target.value)}
                            className="w-full p-2 text-sm outline-none text-black"
                          />
                        </div>
                        {countries
                          .filter((c) =>
                            c.name.common
                              .toLowerCase()
                              .includes(countrySearch.toLowerCase())
                          )
                          .map((c, i) => (
                            <li
                              key={i}
                              onClick={() => handleCountrySelect(c)}
                              className="px-4 py-2 hover:bg-slate-800 cursor-pointer"
                            >
                              {c.flag} {c.name.common}
                            </li>
                          ))}
                      </motion.ul>
                    )}
                  </AnimatePresence>
                </div>

                <textarea
                  name="comments"
                  value={formData.comments}
                  onChange={handleChange}
                  rows="4"
                  placeholder="Comments"
                  className="input-class col-span-2"
                />

                <div className="col-span-2 w-full flex justify-between flex-wrap">
                  {errors.general && (
                    <p className="text-red-400 mt-2 col-span-2">
                      {errors.general}
                    </p>
                  )}
                  {successMessage && (
                    <p className="text-green-500 mt-2 col-span-2">
                      {successMessage}
                    </p>
                  )}

                  <button
                    type="submit"
                    disabled={loading}
                    className="bg-gray-800 justify-end px-6 py-2 rounded-lg hover:bg-gray-900 transition disabled:opacity-50"
                  >
                    {loading ? "Submitting..." : "Submit Listing"}
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
